import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency } from '../../utils/formatters';

interface AnimatedNumberDisplayProps {
  value: number;
  currencyFormat: 'MXN' | 'USD' | 'EUR';
  className?: string;
  duration?: number;
  staggerDelay?: number;
  isCurrency?: boolean; // New prop to control currency formatting
}

const AnimatedNumberDisplay: React.FC<AnimatedNumberDisplayProps> = ({
  value,
  currencyFormat,
  className = '',
  duration = 1.2,
  staggerDelay = 0.08,
  isCurrency = true // Default to true for backward compatibility
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (value !== displayValue) {
      setIsAnimating(true);
      
      // Animate the number change with a slight delay to create the train station effect
      const timer = setTimeout(() => {
        setDisplayValue(value);
        setTimeout(() => setIsAnimating(false), duration * 1000);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [value, displayValue, duration]);

  // Format the currency and split into characters
  const formattedValue = isCurrency ? 
    formatCurrency(displayValue, currencyFormat) : 
    Math.round(displayValue).toLocaleString();
  const characters = formattedValue.split('');

  return (
    <div className={`inline-flex items-center ${className}`}>
      <AnimatePresence mode="wait">
        {characters.map((char, index) => {
          const isDigit = /\d/.test(char);
          
          return (
            <motion.span
              key={`${displayValue}-${index}-${char}`}
              className={`inline-block ${isDigit ? 'font-mono' : ''}`}
              initial={{ 
                y: isDigit ? -20 : 0, 
                opacity: isDigit ? 0 : 1,
                rotateX: isDigit ? -90 : 0,
                scale: isDigit ? 0.8 : 1
              }}
              animate={{ 
                y: 0, 
                opacity: 1,
                rotateX: 0,
                scale: 1
              }}
              exit={{ 
                y: isDigit ? 20 : 0, 
                opacity: isDigit ? 0 : 1,
                rotateX: isDigit ? 90 : 0,
                scale: isDigit ? 0.8 : 1
              }}
              transition={{
                duration: duration,
                delay: isDigit ? index * staggerDelay : 0,
                ease: [0.25, 0.1, 0.25, 1],
                type: "spring",
                stiffness: 200,
                damping: 25
              }}
              style={{
                transformOrigin: 'center center',
                transformStyle: 'preserve-3d'
              }}
            >
              {char}
            </motion.span>
          );
        })}
      </AnimatePresence>
      
      {/* Subtle glow effect when animating */}
      {isAnimating && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-200 to-transparent opacity-30 pointer-events-none"
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
        />
      )}
    </div>
  );
};

export default AnimatedNumberDisplay;